import React from 'react';
import { Checkbox, Flex, LoadingOrbit, Stack, TagBadge, Text } from '@chatsdet/ui';
import { useOrgCredentials } from '../../hooks/useOrgCredentials';
import { usePropertyCredentialSummaries } from '../../hooks/usePropertyMetadata';
import { BRAND } from '../../brand';

type CredentialOption = {
  id: string;
  name: string;
  verified: boolean;
  subtitle: string | null;
  needsObservation: boolean;
};

export type CredentialAccessMetadata = {
  role: string | null;
  tenantName: string | null;
  planName: string | null;
  status: string;
};

type Props = {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  isDisabled?: boolean;
  className?: string;
  accessMetadata?: Map<string, CredentialAccessMetadata>;
} & ({ propertyId: string } | { propertyId?: undefined });

export function CredentialMultiSelect(props: Props) {
  const { selectedIds, onChange, isDisabled, className, accessMetadata } = props;
  const name = BRAND.name;
  const propertyId = 'propertyId' in props ? props.propertyId : undefined;

  const orgQuery = useOrgCredentials({ enabled: !propertyId });
  const propertyQuery = usePropertyCredentialSummaries(propertyId ?? '');

  const isLoading = propertyId ? propertyQuery.isLoading : orgQuery.isLoading;

  const options: CredentialOption[] = React.useMemo(() => {
    if (propertyId) {
      const items = propertyQuery.data?.items ?? [];
      return items.map((c) => {
        const meta = accessMetadata?.get(c.id);
        const subtitleParts = [meta?.tenantName, meta?.role, meta?.planName].filter(
          (value): value is string => Boolean(value),
        );
        return {
          id: c.id,
          name: c.name,
          verified: Boolean(c.lastLoginSucceededAt || c.storageStateS3Key),
          subtitle: subtitleParts.length > 0 ? subtitleParts.join(' · ') : null,
          needsObservation: meta?.status === 'seeded' || meta === undefined,
        };
      });
    }
    return orgQuery.credentials.map((c) => ({
      id: c.id,
      name: c.name,
      verified: Boolean(c.lastLoginSucceededAt || c.storageStateS3Key),
      subtitle: c.propertyName ?? null,
      needsObservation: false,
    }));
  }, [propertyId, propertyQuery.data, orgQuery.credentials, accessMetadata]);

  const selected = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (id: string) => {
    if (selected.has(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (isLoading) {
    return (
      <Flex align="center" gap="sm" className={className}>
        <LoadingOrbit size="sm" />
        <Text size="sm" className="text-muted-foreground">
          Loading credentials…
        </Text>
      </Flex>
    );
  }

  if (options.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No credentials available yet. Add one from the Credentials page first.
      </Text>
    );
  }

  return (
    <Stack gap="xs" className={className}>
      {options.map((option) => {
        const isChecked = selected.has(option.id);
        return (
          <label
            key={option.id}
            className={`flex items-start gap-3 rounded-md border p-3 transition cursor-pointer ${
              isChecked
                ? 'border-primary bg-primary/5'
                : 'border-border hover:bg-muted/40'
            }`}
          >
            <Checkbox
              isSelected={isChecked}
              onChange={() => toggle(option.id)}
              isDisabled={isDisabled}
              aria-label={option.name}
            />
            <div className="flex-1 min-w-0">
              <Flex align="center" gap="sm">
                <Text size="sm" className="font-medium text-foreground truncate">
                  {option.name}
                </Text>
                {option.verified ? (
                  <TagBadge name="Verified" color="blue" hideDot />
                ) : null}
              </Flex>
              {option.subtitle ? (
                <Text size="xs" className="text-muted-foreground truncate">
                  {option.subtitle}
                </Text>
              ) : option.needsObservation && propertyId ? (
                <Text size="xs" className="text-muted-foreground italic">
                  Role &amp; tenant unknown — {name} will discover on first run.
                </Text>
              ) : null}
            </div>
          </label>
        );
      })}
    </Stack>
  );
}
